import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import { Settings, MonetizationOn, Security, Notifications, Person, AdminPanelSettings } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import AdSettings from '../components/Settings/AdSettings';
import PrivacySettings from '../components/Privacy/PrivacySettings';
import ProfilePictureUpload from '../components/Settings/ProfilePictureUpload';
import { useAuth } from "../contexts/AuthContext";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `settings-tab-${index}`,
    'aria-controls': `settings-tabpanel-${index}`,
  };
}

/**
 * Profile Settings Component
 * Handles profile picture upload and management
 */
const ProfileSettings: React.FC = () => {
  const { user, isLoading, isAuthenticated, updateUser, getFreshToken } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Redirect if not authenticated
  if (!isAuthenticated && !isLoading) {
    return (
      <Alert severity="warning">
        Please log in to access profile settings.
      </Alert>
    );
  }

  const handleUploadSuccess = (imageUrl: string) => {
    setSuccessMessage('Profile picture uploaded successfully!');
    setError(null);
    
    // Update user state in AuthContext with the try-on profile picture URL
    updateUser({
      tryOnProfilePictureUrl: imageUrl,
      tryOnPictureUploadedAt: new Date(),
    });
    
    console.log('✅ Profile picture URL updated in user state:', imageUrl);

    // Clear success message after 5 seconds
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  const handleUploadError = (errorMessage: string) => {
    setError(errorMessage);
    setSuccessMessage(null);
  };

  const handleDelete = () => {
    setSuccessMessage('Profile picture deleted successfully!');
    setError(null);
    
    // Update user state in AuthContext - clear try-on profile picture
    updateUser({
      tryOnProfilePictureUrl: undefined,
      tryOnPictureUploadedAt: undefined,
    });
    
    console.log('✅ Profile picture cleared from user state');

    // Clear success message after 5 seconds
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Profile Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage your profile picture for virtual try-on functionality.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Left pane layout for profile picture upload */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' },
        gap: 3,
        alignItems: 'flex-start'
      }}>
        {/* Left pane - Profile Picture Upload */}
        <Box sx={{ 
          flex: { xs: '1 1 100%', md: '0 0 400px' },
          maxWidth: { xs: '100%', md: '400px' }
        }}>
          <ProfilePictureUpload
            currentImageUrl={user?.tryOnProfilePictureUrl}
            onUploadSuccess={handleUploadSuccess}
            onUploadError={handleUploadError}
            onDelete={handleDelete}
          />
        </Box>

        {/* Right pane - Additional profile settings (future expansion) */}
        <Box sx={{ 
          flex: 1,
          minWidth: 0
        }}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Try-On Settings
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Your try-on profile picture is private and only visible to you. It's used exclusively for virtual try-on functionality.
            </Typography>
            
            {user?.tryOnPictureUploadedAt && (
              <Typography variant="body2" color="text.secondary">
                Last updated: {user.tryOnPictureUploadedAt.toLocaleDateString()}
              </Typography>
            )}
            
            {!user?.tryOnProfilePictureUrl && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Upload a profile picture to start using the virtual try-on feature on outfit cards.
              </Alert>
            )}
          </Paper>

          <Paper elevation={1} sx={{ p: 3, mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Gender Preference
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Set your gender to personalize search results. Outfits matching your gender will be shown first.
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Gender</InputLabel>
              <Select
                value={user?.gender || ''}
                label="Gender"
                onChange={async (e: SelectChangeEvent) => {
                  const gender = e.target.value;
                  // Optimistically update local state
                  updateUser({ gender });
                  // Persist to localStorage for search prioritization
                  localStorage.setItem('userGender', gender);

                  // Persist to backend database via PATCH /auth/me
                  try {
                    const token = await getFreshToken();
                    const response = await fetch('https://a3d1kmonz2.execute-api.us-east-1.amazonaws.com/dev/auth/me', {
                      method: 'PATCH',
                      headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify({ gender }),
                    });

                    if (!response.ok) {
                      const errorData = await response.json().catch(() => ({}));
                      console.error('❌ Failed to save gender preference:', response.status, errorData);
                      setError('Failed to save gender preference. Please try again.');
                      setTimeout(() => setError(null), 5000);
                      return;
                    }

                    console.log('✅ Gender preference saved to backend:', gender);
                    if (gender) {
                      setSuccessMessage(`Gender set to ${gender}. Search results will now prioritize ${gender.toLowerCase()} outfits.`);
                    } else {
                      setSuccessMessage('Gender preference cleared.');
                    }
                    setTimeout(() => setSuccessMessage(null), 5000);
                  } catch (err) {
                    console.error('❌ Error saving gender preference:', err);
                    setError('Failed to save gender preference. Please try again.');
                    setTimeout(() => setError(null), 5000);
                  }
                }}
              >
                <MenuItem value="">Not specified</MenuItem>
                <MenuItem value="Men">Male</MenuItem>
                <MenuItem value="Women">Female</MenuItem>
              </Select>
            </FormControl>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Settings Page Component
 * Main settings page with tabs for different setting categories
 */
/**
 * Admin panel with links to admin tools
 */
const AdminPanel: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Admin Tools
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Access admin-only features and tools.
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.hover' },
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
          onClick={() => navigate('/admin/bulk-upload')}
        >
          <AdminPanelSettings color="primary" />
          <Box>
            <Typography variant="subtitle1">Bulk Upload</Typography>
            <Typography variant="body2" color="text.secondary">
              Upload multiple outfits at once on behalf of any user
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

const SettingsPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 4 }}>
        Settings
      </Typography>

      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="settings tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab
              icon={<Person />}
              label="Profile"
              {...a11yProps(0)}
              sx={{ minHeight: 72 }}
            />
            <Tab
              icon={<MonetizationOn />}
              label="Ad Settings"
              {...a11yProps(1)}
              sx={{ minHeight: 72 }}
            />
            <Tab
              icon={<Security />}
              label="Privacy"
              {...a11yProps(2)}
              sx={{ minHeight: 72 }}
            />
            <Tab
              icon={<Settings />}
              label="General"
              {...a11yProps(3)}
              sx={{ minHeight: 72 }}
            />
            <Tab
              icon={<Notifications />}
              label="Notifications"
              {...a11yProps(4)}
              sx={{ minHeight: 72 }}
            />
            <Tab
              icon={<AdminPanelSettings />}
              label="Admin"
              {...a11yProps(5)}
              sx={{ minHeight: 72 }}
            />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <ProfileSettings />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <AdSettings />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <PrivacySettings />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Box>
            <Typography variant="h6" gutterBottom>
              General Settings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              General application settings will be available here.
            </Typography>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Notification Settings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Notification preferences will be available here.
            </Typography>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={5}>
          <AdminPanel />
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default SettingsPage;